/**
 * Keyboard shortcut utilities for workspace layout
 * 
 * Provides global keyboard shortcuts for toggling workspace panels:
 * - Ctrl/Cmd + [ : Toggle My Courses panel
 * - Ctrl/Cmd + ] : Toggle Scout's Desk panel
 */

/**
 * Check if the event target is an editable element
 * Prevents shortcuts from firing while user is typing
 * 
 * @param {HTMLElement} element - Event target element
 * @returns {boolean} True if element is editable
 */
export function isEditableTarget(element) {
  if (!element) return false;
  
  // Check if it's an input-like element
  if (element.tagName?.match(/INPUT|TEXTAREA|SELECT/)) {
    return true;
  }
  
  // Check if it's contentEditable
  if (element.isContentEditable) {
    return true;
  }
  
  // Check if it's inside a contentEditable container
  if (element.closest && element.closest('[contenteditable="true"]')) {
    return true;
  }
  
  return false;
}

/**
 * Setup global keyboard shortcuts for workspace panel toggling
 * 
 * @param {Object} handlers - Handler functions
 * @param {Function} handlers.onToggleCourses - Called when Ctrl/Cmd + [ pressed
 * @param {Function} handlers.onToggleDesk - Called when Ctrl/Cmd + ] pressed
 * @returns {Function} Cleanup function to remove event listener
 */
export function setupWorkspaceShortcuts({ onToggleCourses, onToggleDesk }) {
  const handleKeyDown = (e) => {
    // Only handle Ctrl/Cmd key combinations
    if (!e.ctrlKey && !e.metaKey) {
      return;
    }
    
    // Don't fire if user is typing in an input
    if (isEditableTarget(e.target)) {
      return;
    }
    
    // Handle specific keys
    switch (e.key) {
      case '[':
        e.preventDefault();
        onToggleCourses?.();
        break;
      
      case ']':
        e.preventDefault();
        onToggleDesk?.();
        break;
      
      // Optional: Add more shortcuts here
      // case '\\':
      //   e.preventDefault();
      //   onToggleFocusMode?.();
      //   break;
      
      default:
        // Not a handled shortcut
        break;
    }
  };
  
  // Add global listener
  window.addEventListener('keydown', handleKeyDown);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}
